import { Fraction } from '@learnforge/demo-math/numeric';
import { type Expr, num, variable, add, mul, pow } from './expr';

export class Polynomial {
  readonly coeffs: readonly Fraction[];
  readonly varName: string;

  constructor(coeffs: readonly Fraction[], varName: string) {
    // Copy coefficients and strip trailing zeros
    const arr = Array.from(coeffs);

    // Pop trailing zeros (a Fraction is zero when .num === 0)
    while (arr.length > 0 && arr[arr.length - 1]!.num === 0) {
      arr.pop();
    }

    this.coeffs = Object.freeze(arr);
    this.varName = varName;
  }

  static zero(varName: string): Polynomial {
    return new Polynomial([], varName);
  }

  static fromExpr(expr: Expr, varName: string): Polynomial | null {
    const result = fromExprRec(expr, varName);
    if (result === null) return null;
    return new Polynomial(result, varName);
  }

  static linearFromRoot(root: Fraction, varName: string = 'x'): Polynomial {
    return new Polynomial([root.neg(), Fraction.ONE], varName);
  }

  toExpr(): Expr {
    const terms: Expr[] = [];

    for (let i = 0; i < this.coeffs.length; i += 1) {
      const coeff = this.coeffs[i]!;
      if (coeff.num === 0) continue; // Skip zero terms

      let term: Expr;

      if (i === 0) {
        // Constant term
        term = num(coeff);
      } else if (i === 1) {
        // Linear term: coeff * x
        term = mul(num(coeff), variable(this.varName));
      } else {
        // Higher degree: coeff * x^i
        term = mul(num(coeff), pow(variable(this.varName), num(i)));
      }

      terms.push(term);
    }

    if (terms.length === 0) {
      return num(Fraction.ZERO);
    }

    if (terms.length === 1) {
      return terms[0]!;
    }

    return add(...terms);
  }

  add(other: Polynomial): Polynomial {
    const maxLen = Math.max(this.coeffs.length, other.coeffs.length);
    const result: Fraction[] = [];

    for (let i = 0; i < maxLen; i += 1) {
      const a = i < this.coeffs.length ? this.coeffs[i]! : Fraction.ZERO;
      const b = i < other.coeffs.length ? other.coeffs[i]! : Fraction.ZERO;
      result.push(a.add(b));
    }

    return new Polynomial(result, this.varName);
  }

  sub(other: Polynomial): Polynomial {
    const maxLen = Math.max(this.coeffs.length, other.coeffs.length);
    const result: Fraction[] = [];

    for (let i = 0; i < maxLen; i += 1) {
      const a = i < this.coeffs.length ? this.coeffs[i]! : Fraction.ZERO;
      const b = i < other.coeffs.length ? other.coeffs[i]! : Fraction.ZERO;
      result.push(a.sub(b));
    }

    return new Polynomial(result, this.varName);
  }

  mul(other: Polynomial): Polynomial {
    if (this.isZero || other.isZero) {
      return Polynomial.zero(this.varName);
    }

    // Convolution: result[i] = sum of a[j] * b[i-j]
    const result: Fraction[] = Array.from(
      { length: this.coeffs.length + other.coeffs.length - 1 },
      () => Fraction.ZERO,
    );

    for (let i = 0; i < this.coeffs.length; i += 1) {
      for (let j = 0; j < other.coeffs.length; j += 1) {
        const prod = this.coeffs[i]!.mul(other.coeffs[j]!);
        result[i + j] = result[i + j]!.add(prod);
      }
    }

    return new Polynomial(result, this.varName);
  }

  scale(factor: Fraction): Polynomial {
    const result = this.coeffs.map((c) => c.mul(factor));
    return new Polynomial(result, this.varName);
  }

  divmod(other: Polynomial): { quotient: Polynomial; remainder: Polynomial } {
    if (other.isZero) {
      throw new Error('Polynomial.divmod: division by zero polynomial');
    }

    let remainder = new Polynomial(Array.from(this.coeffs), this.varName);
    const quotient: Fraction[] = [];

    while (!remainder.isZero && remainder.degree >= other.degree) {
      // Divide leading terms
      const coeff = remainder.leadingCoefficient.div(other.leadingCoefficient);
      const degDiff = remainder.degree - other.degree;

      // Add coeff * x^degDiff to quotient
      if (quotient.length < degDiff + 1) {
        quotient.length = degDiff + 1;
      }
      quotient[degDiff] = coeff;

      // Subtract coeff * x^degDiff * other from remainder
      const toSubtract = other.scale(coeff);
      const padded: Fraction[] = Array.from(
        { length: degDiff + toSubtract.coeffs.length },
        () => Fraction.ZERO,
      );
      for (let i = 0; i < toSubtract.coeffs.length; i += 1) {
        padded[i + degDiff] = toSubtract.coeffs[i]!;
      }

      remainder = remainder.sub(new Polynomial(padded, this.varName));
    }

    // Fill in zeros for missing quotient terms
    if (quotient.length === 0) {
      quotient.push(Fraction.ZERO);
    }

    return {
      quotient: new Polynomial(quotient, this.varName),
      remainder,
    };
  }

  gcd(other: Polynomial): Polynomial {
    if (other.isZero) {
      return this.toMonic().monic;
    }

    // Euclid: gcd(this, other) = gcd(other, this mod other). Seed the loop with
    // the first reduction so we never alias `this` to a local (no-this-alias).
    let a: Polynomial = other;
    let b: Polynomial = this.divmod(other).remainder;

    while (!b.isZero) {
      const remainder = a.divmod(b).remainder;
      a = b;
      b = remainder;
    }

    // Return a as monic
    return a.toMonic().monic;
  }

  derivative(): Polynomial {
    if (this.coeffs.length <= 1) {
      return Polynomial.zero(this.varName);
    }

    const result: Fraction[] = [];

    for (let i = 1; i < this.coeffs.length; i += 1) {
      result.push(this.coeffs[i]!.mul(new Fraction(i, 1)));
    }

    return new Polynomial(result, this.varName);
  }

  evaluate(x: Fraction): Fraction {
    // Horner's method
    if (this.coeffs.length === 0) {
      return Fraction.ZERO;
    }

    let result = this.coeffs[this.coeffs.length - 1]!;

    for (let i = this.coeffs.length - 2; i >= 0; i -= 1) {
      result = result.mul(x).add(this.coeffs[i]!);
    }

    return result;
  }

  get degree(): number {
    if (this.coeffs.length === 0) {
      return -Infinity;
    }
    return this.coeffs.length - 1;
  }

  get leadingCoefficient(): Fraction {
    if (this.coeffs.length === 0) {
      return Fraction.ZERO;
    }
    return this.coeffs[this.coeffs.length - 1]!;
  }

  get isZero(): boolean {
    return this.coeffs.length === 0;
  }

  get isMonic(): boolean {
    if (this.isZero) {
      return false;
    }
    return this.leadingCoefficient.equals(Fraction.ONE);
  }

  toMonic(): { monic: Polynomial; scale: Fraction } {
    if (this.isZero) {
      return { monic: this, scale: Fraction.ONE };
    }

    const scale = this.leadingCoefficient;
    const reciprocal = scale.reciprocal();
    const monic = this.scale(reciprocal);

    return { monic, scale };
  }

  equals(other: Polynomial): boolean {
    if (this.varName !== other.varName) {
      return false;
    }
    if (this.coeffs.length !== other.coeffs.length) {
      return false;
    }
    return this.coeffs.every((coeff, i) => coeff.equals(other.coeffs[i]!));
  }
}

function fromExprRec(expr: Expr, varName: string): Fraction[] | null {
  switch (expr.kind) {
    case 'num': {
      return [expr.value];
    }

    case 'var': {
      if (expr.name === varName) {
        return [Fraction.ZERO, Fraction.ONE];
      }
      return null;
    }

    case 'add': {
      let result: Fraction[] | null = null;

      for (const child of expr.children) {
        const childPoly = fromExprRec(child, varName);
        if (childPoly === null) {
          return null;
        }

        if (result === null) {
          result = Array.from(childPoly);
        } else {
          // Add childPoly to result
          const maxLen = Math.max(result.length, childPoly.length);
          for (let i = 0; i < maxLen; i += 1) {
            const a = i < result.length ? result[i]! : Fraction.ZERO;
            const b = i < childPoly.length ? childPoly[i]! : Fraction.ZERO;
            if (i < result.length) {
              result[i] = a.add(b);
            } else {
              result.push(a.add(b));
            }
          }
        }
      }

      return result ?? [Fraction.ZERO];
    }

    case 'mul': {
      let result: Fraction[] | null = null;

      for (const child of expr.children) {
        const childPoly = fromExprRec(child, varName);
        if (childPoly === null) {
          return null;
        }

        if (result === null) {
          result = Array.from(childPoly);
        } else {
          // Multiply result by childPoly via convolution
          const newResult: Fraction[] = Array.from(
            { length: result.length + childPoly.length - 1 },
            () => Fraction.ZERO,
          );

          for (let i = 0; i < result.length; i += 1) {
            for (let j = 0; j < childPoly.length; j += 1) {
              const prod = result[i]!.mul(childPoly[j]!);
              newResult[i + j] = newResult[i + j]!.add(prod);
            }
          }

          result = newResult;
        }
      }

      return result ?? [Fraction.ONE];
    }

    case 'pow': {
      const basePoly = fromExprRec(expr.base, varName);
      if (basePoly === null) {
        return null;
      }

      // Check if exponent is a non-negative integer
      if (expr.exp.kind !== 'num') {
        return null;
      }

      const expFrac = expr.exp.value;
      if (!expFrac.isInteger() || expFrac.num < 0) {
        return null;
      }

      const exp = expFrac.num;

      // Compute basePoly^exp via repeated multiplication
      let result: Fraction[] = [Fraction.ONE];

      for (let i = 0; i < exp; i += 1) {
        const newResult: Fraction[] = Array.from(
          { length: result.length + basePoly.length - 1 },
          () => Fraction.ZERO,
        );

        for (let j = 0; j < result.length; j += 1) {
          for (let k = 0; k < basePoly.length; k += 1) {
            const prod = result[j]!.mul(basePoly[k]!);
            newResult[j + k] = newResult[j + k]!.add(prod);
          }
        }

        result = newResult;
      }

      return result;
    }

    case 'func':
    case 'sigma': {
      return null;
    }
  }
}
